import { useEffect, useRef } from 'react';
import axios from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '@shared/stores/authStore';
import { secureStorage } from '@shared/lib/secureStorage';

const REFRESH_INTERVAL_MS = 25 * 60_000;

async function refreshAccessToken(): Promise<void> {
  const auth = useAuthStore.getState();
  if (auth.isApiKeyAuth()) return;

  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) return;

  const baseURL = auth.getRegionalApiUrl() || env.apiBaseUrl;
  const { data } = await axios.post(
    `${baseURL}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const accessToken = data.accessToken ?? data.access_token;
  const nextRefresh = data.refreshToken ?? data.refresh_token ?? refreshToken;
  if (!accessToken) return;

  useAuthStore.setState({ accessToken, refreshToken: nextRefresh });
  try {
    localStorage.setItem('paas_access_token', accessToken);
  } catch {
    /* noop */
  }
  await secureStorage.set('paas_refresh_token', nextRefresh);
}

/**
 * Proactive JWT refresh (~50% of a typical 1h token lifetime).
 * API key mode skips refresh entirely.
 */
export function useGuardTokenRefresh(): void {
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const tick = () => {
      void refreshAccessToken().catch((err) => {
        console.warn('[guard] proactive refresh failed:', err);
      });
    };

    if (!useAuthStore.getState().isApiKeyAuth() && useAuthStore.getState().getRefreshToken()) {
      tick();
      timer.current = setInterval(tick, REFRESH_INTERVAL_MS);
    }

    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);
}

export { refreshAccessToken };
